<template>
  <div class="glossary-view">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">
            <i class="fas fa-book mr-2" />
            {{ $t('glossary.hero-badge') }}
          </div>
          <h1 class="hero-title">
            {{ $t('glossary.hero-title') }}
          </h1>
          <p class="hero-subtitle">
            {{ $t('glossary.hero-subtitle') }}
          </p>
          <p class="hero-meta">
            {{ $t('glossary.hero-count', { count: terms.length }) }}
          </p>
        </div>
      </div>
    </section>

    <!-- Alphabet Navigation -->
    <section class="alphabet-section">
      <div class="container">
        <p class="alphabet-label">
          {{ $t('glossary.alphabet-label') }}
        </p>
        <div
          class="alphabet-nav"
          role="navigation"
          :aria-label="$t('glossary.alphabet-label')"
        >
          <a
            v-for="letter in alphabet"
            :key="letter"
            :href="hasLetter(letter) ? `#letter-${letter}` : undefined"
            :class="['letter', { 'is-disabled': !hasLetter(letter) }]"
            :aria-disabled="!hasLetter(letter)"
            @click.prevent="hasLetter(letter) && scrollToLetter(letter)"
          >
            {{ letter }}
          </a>
        </div>
      </div>
    </section>

    <!-- Glossary Terms -->
    <section class="terms-section">
      <div class="container">
        <div
          v-for="letter in availableLetters"
          :id="`letter-${letter}`"
          :key="letter"
          class="letter-group"
        >
          <h2 class="letter-heading">
            <span class="letter-marker">{{ letter }}</span>
          </h2>
          <div class="terms-grid">
            <article
              v-for="term in termsByLetter[letter]"
              :id="`term-${term.key}`"
              :key="term.key"
              class="term-card"
            >
              <header class="term-header">
                <h3 class="term-title">
                  {{ $t(`glossary.terms.${term.key}.term`) }}
                </h3>
                <a
                  :href="`#term-${term.key}`"
                  class="term-anchor"
                  :aria-label="$t('glossary.copy-link')"
                  @click.prevent="copyAnchor(term.key)"
                >
                  <i class="fas fa-link" />
                </a>
              </header>
              <p class="term-definition">
                {{ $t(`glossary.terms.${term.key}.definition`) }}
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-card">
          <h3>{{ $t('glossary.cta-title') }}</h3>
          <p>{{ $t('glossary.cta-subtitle') }}</p>
          <router-link
            to="/auth/signup?from=v4_glossary"
            class="cta-button"
          >
            <i class="fas fa-rocket mr-2" />
            {{ $t('glossary.cta-button') }}
          </router-link>
        </div>
      </div>
    </section>

    <button
      v-show="showBackToTop"
      class="back-to-top"
      :aria-label="$t('glossary.back-to-top')"
      @click="scrollToTop"
    >
      <i class="fas fa-arrow-up" />
    </button>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePageSeo } from '@/composables/usePageSeo';
import { SITE_URL, SITE_NAME, buildBreadcrumbSchema } from '@/config/seo';

const { t, locale } = useI18n();

// Stable list of glossary terms. The first letter is computed from the
// English term to keep navigation and grouping consistent across languages.
const terms = [
  { key: 'aggregator', letter: 'A' },
  { key: 'album', letter: 'A' },
  { key: 'algorithmic-playlist', letter: 'A' },
  { key: 'catalog', letter: 'C' },
  { key: 'composition', letter: 'C' },
  { key: 'content-id', letter: 'C' },
  { key: 'copyright', letter: 'C' },
  { key: 'cover-art', letter: 'C' },
  { key: 'cover-song', letter: 'C' },
  { key: 'distribution', letter: 'D' },
  { key: 'dsp', letter: 'D' },
  { key: 'editorial-playlist', letter: 'E' },
  { key: 'ep', letter: 'E' },
  { key: 'featuring', letter: 'F' },
  { key: 'isrc', letter: 'I' },
  { key: 'lead-time', letter: 'L' },
  { key: 'lossless-audio', letter: 'L' },
  { key: 'master-recording', letter: 'M' },
  { key: 'mastering', letter: 'M' },
  { key: 'mechanical-royalties', letter: 'M' },
  { key: 'metadata', letter: 'M' },
  { key: 'mixing', letter: 'M' },
  { key: 'monthly-listeners', letter: 'M' },
  { key: 'net-royalties', letter: 'N' },
  { key: 'payout', letter: 'P' },
  { key: 'performance-royalties', letter: 'P' },
  { key: 'pre-order', letter: 'P' },
  { key: 'pre-save', letter: 'P' },
  { key: 'pro', letter: 'P' },
  { key: 'publishing-rights', letter: 'P' },
  { key: 'release-date', letter: 'R' },
  { key: 'remix', letter: 'R' },
  { key: 'royalties', letter: 'R' },
  { key: 'royalty-split', letter: 'R' },
  { key: 'single', letter: 'S' },
  { key: 'smart-link', letter: 'S' },
  { key: 'sound-recording', letter: 'S' },
  { key: 'spatial-audio', letter: 'S' },
  { key: 'split-sheet', letter: 'S' },
  { key: 'stream', letter: 'S' },
  { key: 'sync-license', letter: 'S' },
  { key: 'takedown', letter: 'T' },
  { key: 'upc', letter: 'U' },
];

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const termsByLetter = computed(() => {
  const groups = {};
  for (const term of terms) {
    if (!groups[term.letter]) groups[term.letter] = [];
    groups[term.letter].push(term);
  }
  Object.keys(groups).forEach(letter => {
    groups[letter].sort((a, b) => {
      const aTerm = t(`glossary.terms.${a.key}.term`);
      const bTerm = t(`glossary.terms.${b.key}.term`);
      return aTerm.localeCompare(bTerm, locale.value);
    });
  });
  return groups;
});

const availableLetters = computed(() =>
  alphabet.filter(letter => termsByLetter.value[letter]?.length),
);

const hasLetter = (letter) => availableLetters.value.includes(letter);

const scrollToLetter = (letter) => {
  const element = document.getElementById(`letter-${letter}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const copyAnchor = (key) => {
  const url = `${window.location.origin}${window.location.pathname}#term-${key}`;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url);
  }
  history.replaceState(null, '', `#term-${key}`);
};

const showBackToTop = ref(false);
const handleScroll = () => {
  showBackToTop.value = window.scrollY > 600;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });

  // Allow direct linking to a term via URL hash
  if (window.location.hash) {
    const target = document.getElementById(window.location.hash.slice(1));
    if (target) {
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});

const definedTermSetJsonLd = computed(() => {
  const definedTerms = terms.map(term => ({
    '@type': 'DefinedTerm',
    name: t(`glossary.terms.${term.key}.term`),
    description: t(`glossary.terms.${term.key}.definition`),
    inDefinedTermSet: `${SITE_URL}/distribution-glossary`,
    url: `${SITE_URL}/distribution-glossary#term-${term.key}`,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: t('glossary.seo-title'),
    description: t('glossary.seo-description'),
    url: `${SITE_URL}/distribution-glossary`,
    inLanguage: locale.value,
    hasDefinedTerm: definedTerms,
  };
});

const breadcrumbsJsonLd = computed(() => buildBreadcrumbSchema([
  { name: SITE_NAME, url: '/' },
  { name: t('glossary.seo-title'), url: '/distribution-glossary' },
]));

usePageSeo({
  titleKey: 'glossary.seo-title',
  descriptionKey: 'glossary.seo-description',
  keywords: computed(() => t('glossary.seo-keywords')),
  ogType: 'article',
  jsonLd: computed(() => [definedTermSetJsonLd.value, breadcrumbsJsonLd.value]),
});
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.glossary-view {
  min-height: 100vh;
  background-color: var(--color-background, #fafafa);

  .body--dark & {
    background-color: var(--color-background, #0a0a0a);
  }
}

// Hero Section
.hero-section {
  background: linear-gradient(135deg, $indiefy-blue 0%, $indiefy-lavender 100%);
  padding: 5rem 1.5rem 3rem;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px);
    background-size: 30px 30px;
    z-index: 1;
  }

  .hero-content {
    position: relative;
    z-index: 2;
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 0.5rem 1.2rem;
    border-radius: 50px;
    font-weight: 600;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }

  .hero-title {
    font-size: 3rem;
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 1rem;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

    @media (max-width: $tablet) {
      font-size: 2.25rem;
    }

    @media (max-width: $mobile) {
      font-size: 1.75rem;
    }
  }

  .hero-subtitle {
    font-size: 1.25rem;
    max-width: 720px;
    margin: 0 auto 1rem;
    opacity: 0.92;

    @media (max-width: $mobile) {
      font-size: 1rem;
    }
  }

  .hero-meta {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-top: 0.5rem;
  }
}

// Alphabet Navigation
.alphabet-section {
  padding: 2rem 1.5rem 0;
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: var(--color-background, #fafafa);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

  .body--dark & {
    background-color: var(--color-background, #0a0a0a);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }

  .container {
    max-width: 900px;
    margin: 0 auto;
    padding: 1.25rem 0;
  }

  .alphabet-label {
    text-align: center;
    color: var(--color-text-light, #666);
    margin-bottom: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.8rem;
  }

  .alphabet-nav {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.4rem;
  }

  .letter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-background-alt, #f0f1f5);
    color: var(--color-text, #333);
    font-weight: 700;
    font-size: 0.9rem;
    text-decoration: none;
    transition: all 0.25s ease;

    .body--dark & {
      background: var(--color-card-background, #1e1e1e);
      color: var(--color-text, #f1f1f1);
    }

    &:not(.is-disabled):hover {
      background: $indiefy-blue;
      color: white;
      transform: translateY(-2px);
    }

    &.is-disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }
}

// Terms Section
.terms-section {
  padding: 3rem 1.5rem 4rem;

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }
}

.letter-group {
  margin-bottom: 3rem;
  scroll-margin-top: 120px;

  &:last-child {
    margin-bottom: 0;
  }
}

.letter-heading {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .letter-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $indiefy-blue, $indiefy-lavender);
    color: white;
    font-size: 1.5rem;
    font-weight: 800;
    box-shadow: 0 4px 12px rgba($indiefy-blue, 0.25);
  }
}

.terms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.25rem;
}

.term-card {
  background: var(--color-background, white);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--color-border, rgba(0, 0, 0, 0.05));
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  scroll-margin-top: 120px;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    border-color: var(--color-border, rgba(255, 255, 255, 0.05));
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
    border-color: rgba($indiefy-blue, 0.3);
  }

  &:target {
    border-color: $indiefy-blue;
    box-shadow: 0 0 0 3px rgba($indiefy-blue, 0.2);
  }
}

.term-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.term-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: $indiefy-blue;
  margin: 0;
  line-height: 1.3;
}

.term-anchor {
  color: var(--color-text-light, #999);
  opacity: 0;
  transition: opacity 0.25s ease, color 0.25s ease;
  font-size: 0.85rem;
  padding: 0.25rem;

  .term-card:hover & {
    opacity: 1;
  }

  &:hover {
    color: $indiefy-blue;
  }
}

.term-definition {
  color: var(--color-text, #444);
  line-height: 1.65;
  margin: 0;
  font-size: 0.95rem;

  .body--dark & {
    color: var(--color-text, #d8d8d8);
  }
}

// CTA Section
.cta-section {
  padding: 3rem 1.5rem 5rem;
  background: var(--color-background-alt, #f5f6fa);

  .body--dark & {
    background: var(--color-background-alt, #111);
  }
}

.cta-card {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
  background: linear-gradient(135deg, $indiefy-blue 0%, $indiefy-lavender 100%);
  color: white;
  border-radius: 20px;
  padding: 3rem 2rem;
  box-shadow: 0 12px 30px rgba($indiefy-blue, 0.25);

  h3 {
    font-size: 1.85rem;
    font-weight: 800;
    margin-bottom: 0.75rem;

    @media (max-width: $mobile) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 1.05rem;
    margin-bottom: 1.75rem;
    opacity: 0.95;
  }

  .cta-button {
    display: inline-flex;
    align-items: center;
    background: white;
    color: $indiefy-blue;
    padding: 0.95rem 2rem;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 700;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
      color: $indiefy-blue;
    }
  }
}

.back-to-top {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: $indiefy-blue;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba($indiefy-blue, 0.4);
  transition: transform 0.25s ease, background 0.25s ease;
  z-index: 50;

  &:hover {
    background: darken($indiefy-blue, 8%);
    transform: translateY(-3px);
  }
}
</style>
